import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type InitializationState = 'idle' | 'checking' | 'downloading' | 'processing' | 'completed' | 'error'

export const useOfflineOperatorInitStore = defineStore('offlineOperatorInit', () => {
  // State
  const state = ref<InitializationState>('idle')
  const totalOperators = ref(0)
  const processedOperators = ref(0)
  const errorMessage = ref<string | null>(null)
  const isInitializing = ref(false)

  // Computed
  const isLoading = computed(() => 
    ['checking', 'downloading', 'processing'].includes(state.value)
  )
  
  const progressPercentage = computed(() => {
    if (totalOperators.value === 0) return 0
    return Math.round((processedOperators.value / totalOperators.value) * 100)
  })

  const statusMessage = computed(() => {
    switch (state.value) {
      case 'checking':
        return 'Setting up offline operators database...'
      case 'downloading':
        return `Downloading ${totalOperators.value.toLocaleString()} operators...`
      case 'processing':
        return `Setting up offline operators database (${processedOperators.value.toLocaleString()} / ${totalOperators.value.toLocaleString()})`
      case 'completed':
        return 'Offline operators database ready!'
      case 'error':
        return errorMessage.value || 'Failed to initialize offline operators database'
      default:
        return 'Setting up offline operators database...'
    }
  })

  // Actions
  function setState(newState: InitializationState) {
    state.value = newState
  }

  function setProgress(processed: number, total: number) {
    processedOperators.value = processed
    totalOperators.value = total
  }

  function setError(message: string) {
    state.value = 'error'
    errorMessage.value = message
    isInitializing.value = false
  }

  function setCompleted() {
    state.value = 'completed'
    isInitializing.value = false
  }

  function startInitialization() {
    // Guard against re-entrant initialization
    if (isInitializing.value || ['checking', 'downloading', 'processing'].includes(state.value)) {
      console.log('📻 [OPERATORS] Initialization already in progress, skipping duplicate start')
      return
    }
    
    // Set initializing flag immediately to prevent race conditions
    isInitializing.value = true
    state.value = 'checking'
    errorMessage.value = null
    processedOperators.value = 0
    totalOperators.value = 0
  }

  function reset() {
    state.value = 'idle'
    totalOperators.value = 0
    processedOperators.value = 0
    errorMessage.value = null
    isInitializing.value = false
  }

  return {
    // State
    state,
    totalOperators,
    processedOperators,
    errorMessage,
    isInitializing,
    
    // Computed
    isLoading,
    progressPercentage,
    statusMessage,
    
    // Actions
    setState,
    setProgress,
    setError,
    setCompleted,
    startInitialization,
    reset
  }
})